import type { DrawerChild } from '@/features/project-board/components/DetailDrawer';

/**
 * 선행 관계 그래프 — 노드를 선으로 이어 "무엇을 먼저 해야 하고 무엇을 동시에 할 수 있나"를 보여준다.
 *
 * 가로축이 단계다(왼쪽 → 오른쪽으로 선행 → 후행). 같은 열에 있는 task는 서로 의존이 없어 **병렬 처리 가능**하다.
 * 단계는 위상정렬 기반 최장경로로 계산하므로 **자식은 항상 모든 선행보다 오른쪽**에 온다.
 */
export interface GraphLayout {
  levels: DrawerChild[][];
  edges: Array<{ from: string; to: string; blocked: boolean; cyclic: boolean }>;
  /** 문서에 실재하는 순환 참조. 계산에서는 끊고, 화면에는 경고로 드러낸다. */
  cycles: string[][];
}

const NODE_WIDTH = 168;
const NODE_HEIGHT = 26;
const COLUMN_GAP = 92;
const ROW_GAP = 8;
const PADDING = 16;

/** 범례 — 그래프를 여러 개 늘어놓는 화면(백로그)에서는 한 번만 보여준다. */
export function DependencyLegend(): React.JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-1 text-[11px] text-faint">
      <span>
        가로 = 단계(선행 → 후행) · 같은 열은 <strong className="text-ink">병렬 가능</strong>
      </span>
      <span className="text-amber-800">주황 선 = 선행 미완(대기)</span>
      <span className="text-rose-600">빨간 점선 = 순환 참조(단계 계산 제외)</span>
    </div>
  );
}

export function DependencyGraph({
  items,
  onOpen,
  legend = true,
}: {
  items: DrawerChild[];
  onOpen: (child: DrawerChild) => void;
  legend?: boolean;
}): React.JSX.Element {
  const { levels, edges, cycles } = DependencyLayout.of(items);
  if (levels.length === 0) return <div className="p-4 text-xs text-faint">선행 관계가 없다.</div>;

  const position = new Map<string, { x: number; y: number }>();
  levels.forEach((level, column) => {
    level.forEach((child, row) => {
      position.set(child.key, {
        x: PADDING + column * (NODE_WIDTH + COLUMN_GAP),
        y: PADDING + row * (NODE_HEIGHT + ROW_GAP),
      });
    });
  });

  const width = PADDING * 2 + levels.length * (NODE_WIDTH + COLUMN_GAP) - COLUMN_GAP;
  const tallest = Math.max(...levels.map((level) => level.length));
  const height = PADDING * 2 + tallest * (NODE_HEIGHT + ROW_GAP) - ROW_GAP + 24;

  return (
    <div className="flex flex-col gap-2">
      {legend && <DependencyLegend />}
      {/* 순환 경고는 이 그래프에만 해당하므로 범례와 달리 항상 그린다. */}
      {cycles.length > 0 && (
        <div
          className="px-1 text-[11px] text-amber-800"
          title={cycles.map((cycle) => cycle.join(' → ')).join('\n')}
        >
          ⚠ 순환 참조 {cycles.length}건 — 문서 오류(계산에서 제외): {cycles[0].join(' → ')}
        </div>
      )}

      <div className="max-h-[52vh] overflow-auto rounded-md border border-line-soft bg-[#fbfcfd]">
        <svg width={width} height={height} role="img" aria-label="선행 관계 그래프">
          <defs>
            <marker id="dep-arrow" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="6" markerHeight="6" orient="auto">
              <path d="M0,0 L8,4 L0,8 z" className="fill-line" />
            </marker>
            <marker id="dep-arrow-blocked" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="6" markerHeight="6" orient="auto">
              <path d="M0,0 L8,4 L0,8 z" className="fill-amber-500" />
            </marker>
          </defs>

          {levels.map((level, column) => (
            <text
              key={`col-${column}`}
              x={PADDING + column * (NODE_WIDTH + COLUMN_GAP) + NODE_WIDTH / 2}
              y={height - 8}
              textAnchor="middle"
              className="fill-faint text-[10px]"
            >
              {column + 1}단 · {level.length}개
            </text>
          ))}

          {edges.map((edge) => {
            const from = position.get(edge.from);
            const to = position.get(edge.to);
            if (!from || !to) return null;
            const x1 = from.x + NODE_WIDTH;
            const y1 = from.y + NODE_HEIGHT / 2;
            const x2 = to.x;
            const y2 = to.y + NODE_HEIGHT / 2;
            const mid = (x1 + x2) / 2;
            return (
              <path
                key={`${edge.from}->${edge.to}`}
                d={`M${x1},${y1} C${mid},${y1} ${mid},${y2} ${x2},${y2}`}
                fill="none"
                strokeWidth={edge.blocked || edge.cyclic ? 1.4 : 1}
                strokeDasharray={edge.cyclic ? '4 3' : undefined}
                className={edge.cyclic ? 'stroke-rose-400' : edge.blocked ? 'stroke-amber-400' : 'stroke-line'}
                markerEnd={edge.cyclic ? undefined : `url(#${edge.blocked ? 'dep-arrow-blocked' : 'dep-arrow'})`}
              >
                <title>
                  {edge.cyclic
                    ? `순환 참조 — ${edge.to} 와 ${edge.from} 가 서로를 선행으로 지정(문서 오류)`
                    : `${edge.from} → ${edge.to}`}
                </title>
              </path>
            );
          })}

          {levels.flatMap((level) =>
            level.map((child) => {
              const at = position.get(child.key);
              if (!at) return null;
              return (
                <g
                  key={child.key}
                  transform={`translate(${at.x},${at.y})`}
                  onClick={() => onOpen(child)}
                  className="cursor-pointer"
                >
                  <title>{`${child.key} ${child.label}${child.dependsOn.length ? `\n선행: ${child.dependsOn.join(', ')}` : ''}${child.blockedBy.length ? `\n미완 선행: ${child.blockedBy.join(', ')}` : ''}`}</title>
                  <rect
                    width={NODE_WIDTH}
                    height={NODE_HEIGHT}
                    rx={5}
                    className={`${NODE[child.status] ?? NODE.unknown} hover:stroke-accent`}
                    strokeWidth={1}
                  />
                  <text x={7} y={17} className="fill-current font-mono text-[10px] opacity-70">
                    {child.key}
                  </text>
                  <text x={32} y={17} className="fill-current text-[11px]">
                    {clip(child.label)}
                  </text>
                  {child.blockedBy.length > 0 && (
                    <text x={NODE_WIDTH - 12} y={17} className="text-[10px]">
                      ⛔
                    </text>
                  )}
                </g>
              );
            }),
          )}
        </svg>
      </div>
    </div>
  );
}

/** SVG는 자동 줄임이 없어 글자 수로 자른다. 전체 제목은 tooltip으로 본다. */
function clip(label: string): string {
  return label.length > 17 ? `${label.slice(0, 17)}…` : label;
}

const NODE: Record<string, string> = {
  todo: 'fill-white stroke-line text-ink',
  progress: 'fill-amber-50 stroke-amber-300 text-amber-900',
  done: 'fill-emerald-50 stroke-emerald-300 text-emerald-900',
  dropped: 'fill-violet-50 stroke-violet-200 text-violet-900',
  unknown: 'fill-white stroke-line text-muted',
};

export class DependencyLayout {
  /**
   * 단계·간선 계산.
   *
   * 예전 구현은 재귀로 깊이를 구하면서 순환을 만나면 0을 돌려줬는데, 그 값이 메모에 섞여 **자식이 선행보다
   * 앞 단에 놓이는** 결과가 나왔다(실측: ad-system에서 227개 관계 중 4건 위반 — `017 → 055 → 017` 등 문서에
   * 실재하는 순환 4개 때문). 그래서 위상정렬(Kahn)로 바꿨다: 먼저 순환을 찾아 그 간선을 끊고, 남은 DAG에서
   * 최장경로 깊이를 구한다. 이러면 자식 깊이 > 모든 선행 깊이가 보장된다.
   */
  static of(items: DrawerChild[]): GraphLayout {
    const known = new Set(items.map((item) => item.key));
    const parents = new Map(items.map((item) => [item.key, item.dependsOn.filter((id) => known.has(id))]));

    const cycles = DependencyLayout.findCycles(parents);
    const dropped = new Set(cycles.flatMap((cycle) => [`${cycle[cycle.length - 2]}->${cycle[cycle.length - 1]}`]));

    // 순환을 만든 마지막 간선만 끊는다 — 나머지 선행 관계는 그대로 보존한다.
    const effectiveParents = new Map(
      [...parents.entries()].map(([key, ids]) => [key, ids.filter((id) => !dropped.has(`${key}->${id}`))]),
    );

    const depth = new Map<string, number>();
    const pending = new Map([...effectiveParents.entries()].map(([key, ids]) => [key, ids.length]));
    const queue = [...pending.entries()].filter(([, count]) => count === 0).map(([key]) => key);
    for (const key of queue) depth.set(key, 0);

    const children = new Map<string, string[]>();
    for (const [key, ids] of effectiveParents) {
      for (const id of ids) children.set(id, [...(children.get(id) ?? []), key]);
    }

    while (queue.length > 0) {
      const key = queue.shift() as string;
      for (const child of children.get(key) ?? []) {
        depth.set(child, Math.max(depth.get(child) ?? 0, (depth.get(key) ?? 0) + 1));
        const left = (pending.get(child) ?? 0) - 1;
        pending.set(child, left);
        if (left === 0) queue.push(child);
      }
    }

    // 끊어진 간선 때문에 큐에 못 들어간 노드는 마지막 단으로(그림이 비지 않게).
    const maxDepth = Math.max(0, ...[...depth.values()]);
    for (const item of items) if (!depth.has(item.key)) depth.set(item.key, maxDepth);

    const levels: DrawerChild[][] = Array.from({ length: Math.max(...[...depth.values()]) + 1 }, () => []);
    for (const item of items) levels[depth.get(item.key) ?? 0].push(item);

    const edges = items.flatMap((item) =>
      (parents.get(item.key) ?? []).map((id) => ({
        from: id,
        to: item.key,
        blocked: item.blockedBy.includes(id),
        // 순환을 닫는 간선 — 단계 계산에서 뺐으므로 화면에서도 실선으로 그리면 거짓말이 된다.
        cyclic: dropped.has(`${item.key}->${id}`),
      })),
    );

    return { levels: levels.filter((level) => level.length > 0), edges, cycles };
  }

  /** 선행 관계에서 순환을 찾는다. 반환값의 마지막 두 항목이 순환을 닫는 간선이다. */
  static findCycles(parents: Map<string, string[]>): string[][] {
    const cycles: string[][] = [];
    const state = new Map<string, 'open' | 'done'>();
    const path: string[] = [];

    const walk = (key: string): void => {
      if (state.get(key) === 'done') return;
      if (state.get(key) === 'open') {
        cycles.push([...path.slice(path.indexOf(key)), key]);
        return;
      }
      state.set(key, 'open');
      path.push(key);
      for (const parent of parents.get(key) ?? []) walk(parent);
      path.pop();
      state.set(key, 'done');
    };

    for (const key of parents.keys()) walk(key);
    return cycles;
  }
}
